// JobSpecViewer — read-only display of the JobSpec bound to a job.
// Shows phases (with required photos + checklist) and the Bill of Materials.
// No editor, no version controls — execution surface only.
//
// Authoring entry point (Layer 3b): when the current role is in
// SPEC_AUTHORING_EDITORS and the registry source spec is resolvable,
// an "Edit JobSpec" button opens the route-less authoring panel. Bound
// jobs themselves are never mutated — publishing creates a new version
// for future binds.

import { useState } from "react";
import { Camera, CheckSquare, Package, FileWarning, Lock, Pin, AlertTriangle, Pencil, Activity } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  resolveBoundSpec,
  formatSpecRef,
  getLatestCanonicalVersion,
  getJobSpec,
  isLegacyBinding,
  type BoundJobSpec,
} from "@/data/jobSpec";
import { canEditJobSpec } from "@/data/jobSpecAuthoring";
import { useRole } from "@/data/RoleContext";
import { JobSpecAuthoringPanel } from "./JobSpecAuthoringPanel";
import { SpecHealthPanel } from "./SpecHealthPanel";

interface JobSpecViewerProps {
  bound?: BoundJobSpec;
  className?: string;
}

function formatPhotoLabel(key: string): string {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function JobSpecViewer({ bound, className }: JobSpecViewerProps) {
  const { role } = useRole();
  const [editorOpen, setEditorOpen] = useState(false);

  if (!bound) {
    return (
      <Card className={`shadow-none border-dashed ${className ?? ""}`}>
        <CardContent className="p-4 flex items-start gap-3">
          <FileWarning className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              No JobSpec Bound
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              This job has no execution spec attached. New jobs must be bound to a
              canonical JobSpec at creation.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const spec = resolveBoundSpec(bound);

  if (!spec) {
    return (
      <Card className={`shadow-none border-destructive/30 ${className ?? ""}`}>
        <CardContent className="p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-destructive">
            JobSpec Missing
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Bound spec <code>{formatSpecRef(bound)}</code> could not be resolved
            in the registry.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Preserve registry/bound phase order exactly as authored. This avoids
  // coupling UI to legacy phase-key enums when humanized labels are used.
  const orderedPhases = [...spec.phases];

  // Editing operates on the registry source spec (latest canonical for this
  // specId), not the bound snapshot. Bound jobs are immutable; publishes
  // create a new version that future binds will resolve to.
  const latestRegistry = getLatestCanonicalVersion(bound.specId);
  const editableSource = latestRegistry !== undefined
    ? getJobSpec(bound.specId, latestRegistry)
    : undefined;
  const showEdit = canEditJobSpec(role) && !!editableSource;

  return (
    <>
      <Card className={`shadow-none ${className ?? ""}`}>
        <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-[0.12em]">
              Job Spec
            </p>
            <h3 className="text-base font-semibold leading-tight mt-0.5">
              {spec.label}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              <code className="font-mono">{formatSpecRef(bound)}</code> •{" "}
              {spec.archetype}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge
              variant={spec.status === "canonical" ? "default" : "outline"}
              className="text-[10px] uppercase tracking-wider"
            >
              {spec.status}
            </Badge>
            <Badge variant="outline" className="text-[10px] gap-1">
              <Pin className="h-2.5 w-2.5" />
              Pinned to v{bound.versionNumber}
            </Badge>
            {(() => {
              const latest = getLatestCanonicalVersion(bound.specId);
              if (latest !== undefined && latest > bound.versionNumber) {
                return (
                  <Badge variant="outline" className="text-[10px] gap-1 border-amber-500/40 text-amber-600 dark:text-amber-400">
                    Latest is v{latest}
                  </Badge>
                );
              }
              return null;
            })()}
            {isLegacyBinding(bound) && (
              <Badge variant="outline" className="text-[10px] gap-1 border-destructive/40 text-destructive">
                <AlertTriangle className="h-2.5 w-2.5" />
                Legacy spec binding
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px] gap-1">
              <Lock className="h-2.5 w-2.5" />
              Bound {new Date(bound.boundAt).toLocaleDateString()}
            </Badge>
            {showEdit && (
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[10px] px-2 gap-1"
                onClick={() => setEditorOpen(true)}
              >
                <Pencil className="h-3 w-3" />
                Edit JobSpec
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="spec" className="w-full">
          <TabsList className="h-8">
            <TabsTrigger value="spec" className="text-xs gap-1.5">
              <Package className="h-3 w-3" /> Spec
            </TabsTrigger>
            <TabsTrigger value="health" className="text-xs gap-1.5">
              <Activity className="h-3 w-3" /> Spec Health
            </TabsTrigger>
          </TabsList>

          <TabsContent value="spec" className="space-y-4 mt-4">
        {/* Phases */}
        <div className="space-y-3">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em]">
            Execution Phases
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {orderedPhases.map((phase) => (
              <div
                key={phase.name}
                className="rounded-md border bg-muted/20 p-3 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{phase.label}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {phase.requiredPhotos.length} photos •{" "}
                    {phase.checklistItems.length} checks
                  </span>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Camera className="h-3 w-3" /> Required Photos
                  </div>
                  <ul className="space-y-0.5 pl-4">
                    {phase.requiredPhotos.map((p) => (
                      <li
                        key={p}
                        className="text-xs text-foreground/80 list-disc"
                      >
                        {formatPhotoLabel(p)}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <CheckSquare className="h-3 w-3" /> Checklist
                  </div>
                  <ul className="space-y-0.5 pl-4">
                    {phase.checklistItems.map((c) => (
                      <li
                        key={c.id}
                        className="text-xs text-foreground/80 list-disc"
                      >
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* BOM */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5">
              <Package className="h-3 w-3" /> Bill of Materials
            </p>
            <span className="text-[10px] text-muted-foreground">
              {spec.billOfMaterials.length} line items
            </span>
          </div>
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-1.5 w-1/3">SKU</th>
                  <th className="text-left font-medium px-3 py-1.5">Item</th>
                  <th className="text-right font-medium px-3 py-1.5 w-20">Qty</th>
                  <th className="text-left font-medium px-3 py-1.5 w-16">Unit</th>
                </tr>
              </thead>
              <tbody>
                {spec.billOfMaterials.map((item) => (
                  <tr key={item.sku} className="border-t">
                    <td className="px-3 py-1.5 font-mono text-[11px]">{item.sku}</td>
                    <td className="px-3 py-1.5">{item.name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {item.quantity}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">
                      {item.unit ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          </TabsContent>

          <TabsContent value="health" className="mt-4">
            <SpecHealthPanel spec={spec} />
          </TabsContent>
        </Tabs>
        </CardContent>
      </Card>
      {showEdit && editableSource && (
        <JobSpecAuthoringPanel
          baseSpec={editableSource}
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </>
  );
}

export default JobSpecViewer;
